<template>
  <div class="container" v-if="!store.loggedIn">
    <img class="logo" src="/static/img/KovoHome_text_white.svg" alt="">
    <div>
      <div class="resetPasswordContainer cardBackground">
        <h1>Set New Password</h1>

        <div v-if="!tokenValid && !loading" class="errorMessage">
          <svg class="errorIcon"><use href="/static/icons/Warning.svg#Warning"/></svg>
          <p>{{ errorMessage || 'Invalid or expired reset link' }}</p>
          <router-link to="/forgot-password" class="linkButton">
            Request a new reset link
          </router-link>
        </div>

        <div v-if="tokenValid && !resetSuccess">
          <div class="passwordContainer">
            <h3>New Password</h3>
            <div class="inputWrapper">
              <input
                class="loginInput"
                ref="password"
                :type="viewPassword ? 'text' : 'password'"
                v-model="password"
                @keyup.enter="handlePasswordEnter"
                style="border-color: #45403C;"
                placeholder="Enter new password"
              >
              <svg @click="togglePasswordVisibility" class="viewPasswordIcon" :class="viewPassword ? 'colorSwap' : ''">
                <use href="/static/icons/Eye.svg#Eye"/>
              </svg>
            </div>
          </div>

          <div class="passwordContainer">
            <h3>Confirm Password</h3>
            <div class="inputWrapper">
              <input
                class="loginInput"
                ref="confirmPassword"
                :type="viewConfirmPassword ? 'text' : 'password'"
                v-model="confirmPassword"
                @keyup.enter="resetPassword"
                style="border-color: #45403C;"
                placeholder="Confirm new password"
              >
              <svg @click="toggleConfirmPasswordVisibility" class="viewPasswordIcon" :class="viewConfirmPassword ? 'colorSwap' : ''">
                <use href="/static/icons/Eye.svg#Eye"/>
              </svg>
            </div>
          </div>

          <div class="passwordRequirements">
            <p class="requirementTitle">Password must:</p>
            <ul>
              <li :class="{ valid: password.length >= 8 }">Be at least 8 characters long</li>
              <li :class="{ valid: /[A-Z]/.test(password) }">Contain an uppercase letter</li>
              <li :class="{ valid: /[a-z]/.test(password) }">Contain a lowercase letter</li>
              <li :class="{ valid: /[0-9]/.test(password) }">Contain a number</li>
            </ul>
          </div>

          <div>
            <button
              class="specialButton"
              @click="resetPassword"
              :disabled="loading"
              style="color: var(--text-primary); background-color: var(--color-secondary-button)"
            >
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
          </div>
        </div>

        <div v-if="resetSuccess" class="successMessage">
          <svg class="successIcon"><use href="/static/icons/Check.svg#Check"/></svg>
          <p>Your password has been reset successfully!</p>
          <button
            class="specialButton"
            @click="goToLogin"
            style="color: var(--text-primary); background-color: var(--color-secondary-button)"
          >
            Go to Login
          </button>
        </div>

        <div class="backToLogin" v-if="!resetSuccess">
          <router-link to="/login" class="linkButton">
            Back to Login
          </router-link>
        </div>
      </div>
    </div>
    <div></div>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import { useLoginStore } from "@/stores/loginStore";
import APIService from "@/servicehandlers/APIService";
import MessageModal from "@/components/MessageModal.vue";
import LoadingPage from "@/components/LoadingPage.vue";

const apiService = new APIService();

export default {
  name: 'ResetPasswordPage',
  components: {
    LoadingPage,
    MessageModal,
  },
  data(){
    return {
      loading: false,
      tokenValid: false,
      resetSuccess: false,
      errorMessage: '',
      appMessage: {
        text: '',
        type: 'error',
      },
      token: '',
      password: '',
      confirmPassword: '',
      viewPassword: false,
      viewConfirmPassword: false,
      store: useLoginStore()
    }
  },
  mounted() {
    if (this.store.loggedIn){
      this.$router.push({ name: 'dashboard' });
      return;
    }

    // Get token from URL query parameter
    this.token = this.$route.query.token;

    if (!this.token) {
      this.errorMessage = 'No reset token provided';
      return;
    }

    this.verifyToken();
  },
  methods: {
    togglePasswordVisibility(){
      this.viewPassword = !this.viewPassword;
    },
    toggleConfirmPasswordVisibility(){
      this.viewConfirmPassword = !this.viewConfirmPassword;
    },
    async verifyToken(){
      this.loading = true;

      try {
        const response = await apiService.get(`/password-reset/verify/${this.token}`);

        if (response && response.valid) {
          this.tokenValid = true;
        } else {
          this.errorMessage = 'Invalid or expired reset token';
        }
      } catch (error) {
        console.error('Token verification error:', error);
        this.errorMessage = error.response?.data?.error || 'Invalid or expired reset token';
      } finally {
        this.loading = false;
      }
    },
    validatePassword(){
      if (!this.password) {
        this.appMessage.text = 'Please enter a new password';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      if (this.password.length < 8) {
        this.appMessage.text = 'Password must be at least 8 characters long';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      if (!/[A-Z]/.test(this.password)) {
        this.appMessage.text = 'Password must contain an uppercase letter';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      if (!/[a-z]/.test(this.password)) {
        this.appMessage.text = 'Password must contain a lowercase letter';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      if (!/[0-9]/.test(this.password)) {
        this.appMessage.text = 'Password must contain a number';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      if (this.password !== this.confirmPassword) {
        this.appMessage.text = 'Passwords do not match';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
        return false;
      }

      return true;
    },
    async resetPassword(){
      if (!this.validatePassword()) {
        return;
      }

      this.loading = true;

      try {
        const response = await apiService.post('/password-reset/reset', {
          token: this.token,
          password: this.password
        });

        if (response) {
          this.resetSuccess = true;
        }
      } catch (error) {
        console.error('Password reset error:', error);
        this.appMessage.text = error.response?.data?.error || 'Failed to reset password. Please try again.';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      } finally {
        this.loading = false;
      }
    },
    handlePasswordEnter(){
      if (this.confirmPassword){
        this.resetPassword();
      } else {
        this.$refs.confirmPassword.focus();
      }
    },
    goToLogin(){
      this.$router.push({ name: 'login' });
    },
  },
}
</script>

<style scoped>
.colorSwap{
  color: #45403C !important;
  background-color: transparent !important;
}

.logo {
  display: block;
  margin: 2rem auto;
  width: 100%;
  height: auto;
  margin-top: 7rem;
}

.resetPasswordContainer{
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  margin: 30px;
  max-width: 500px;
  border-radius: 12px;
  padding: 20px;
}

.passwordContainer{
  width: 100%;
}

.inputWrapper{
  width: 100%;
  position: relative;
}

.viewPasswordIcon{
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  color: var(--text-secondary);
  background-color: #45403C;
  padding: 7.5px;
  border-radius: 12px;
  cursor: pointer;
}

input{
  font-size: 20px;
  height: 50px;
  width: 100%;
}

h3{
  text-align: left;
  margin-bottom: 10px;
}

.passwordRequirements {
  width: 100%;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 10px;
}

.requirementTitle {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  color: var(--text-primary);
}

.passwordRequirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.passwordRequirements li {
  font-size: 16px;
  color: var(--text-primary);
  opacity: 0.7;
  padding: 5px 0;
}

.passwordRequirements li.valid {
  color: #4CAF50;
  opacity: 1;
}

.passwordRequirements li.valid::before {
  content: '✓ ';
  font-weight: bold;
}

.passwordRequirements li:not(.valid)::before {
  content: '○ ';
}

.successMessage {
  text-align: center;
  padding: 20px;
  color: var(--text-primary);
}

.successIcon {
  width: 60px;
  height: 60px;
  color: #4CAF50;
  margin-bottom: 20px;
}

.errorMessage {
  text-align: center;
  padding: 20px;
  color: var(--text-primary);
}

.errorIcon {
  width: 60px;
  height: 60px;
  color: #f44336;
  margin-bottom: 20px;
}

.backToLogin {
  margin-top: 10px;
}

.linkButton {
  color: var(--color-secondary-button);
  text-decoration: none;
  font-size: 27px;
}

.linkButton:hover {
  text-decoration: underline;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
